<template>
	<LayoutBreadcrumbs class="main-wrapper" />

	<div class="main-wrapper">
		<Category
			v-if="currentCategory && initialData"
			:current-category="currentCategory"
			:initial-data="initialData"
			:page-size="pageSize"
			:picture-aspect-ratio="pictureAspectRatio"
			:category-widgets="categoryWidgets"
			:initial-current-sorting-order="initialCurrentSortingOrder"
			:pre-footer="preFooter"
			:story-data="storyblokDataRef?.content"
			:recommendations="recommendations"
		/>
		<div v-else class="flex flex-col gap-4 mb-10">
			<div class="border-b border-b-gray-lighter pb-6 h-[155px]">
				<h1
					class="text-3xl mb-4 animate-pulse bg-gray-lighter w-[100px] h-10"
				></h1>
				<span
					class="max-w-screen-sm block animate-pulse bg-gray-lighter w-[50px] h-10"
				></span>
			</div>
			<div class="flex-col lg:flex-row justify-between w-full flex">
				<div
					class="lg:w-[280px] relative h-[400px] gap-2 hidden md:flex flex-col"
				>
					<div
						v-for="i in 6"
						:key="i"
						class="h-[50px] bg-gray-light border-gray-lighter border animate-pulse w-full"
					/>
				</div>
				<div class="flex flex-wrap relative flex-1 lg:ml-8">
					<div class="grid grid-cols-2 lg:grid-cols-3 gap-8 w-full">
						<div
							v-for="i in 6"
							:key="i"
							class="h-[300px] md:h-[400px] bg-gray-light border-gray-lighter border animate-pulse w-full"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import type { Category as CategoryType } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	BlokSEOApp,
	BlokCategoryWidget,
	BlokColumns,
	BlokProductRecommendations
} from "~/types/storyblok/storyblok";
import type { InitialData } from "~/types/components/category";
import { computed, ref } from "vue";
import { getStoryblokVersion } from "~/utils/storyblok/getStoryblokVersion";
import { buildStoryblokUrl } from "~/utils/storyblok/buildStoryblokUrl";
import { fetchStoryblokContentByFolder } from "~/utils/storyblok/fetchStoryblokContentByFolder";

const storyblokApi = useStoryblokApi();
const { locale, t, localeProperties } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();

const config = useRuntimeConfig();
const ImagesCdnUrl = config.public.norce.cdn + "/";

const { getCategoryDetailsById } = useCategory();
const { setCategoryBreadcrumb, setPageType, setPageTitle } = usePageStore();
const {
	listProductByFilters,
	getCurrentCategory,
	mapProductFilters,
	kebabCase
} = useNorcePwa();
const { fetchCategoryTree } = useFetchCategoryTree();
const { fetchAllParametrics } = useAllParametrics();

const initialData = ref<InitialData>();
const currentCategory = ref<CategoryType>();
const pageSize = ref<string>("0");
const seo = ref<BlokSEOApp>();
const pictureAspectRatio = ref<string>("4/3");
const initialCurrentSortingOrder = ref<string>(
	(route.query.sort as string) || "SortOrder Desc"
);
const storyblokDataRef = ref();
const recommendations = ref<BlokProductRecommendations | null>(null);

const fetchCategoryData = async () => {
	if (!locale.value) {
		return;
	}

	setPageType("category");

	const categoryTree = await fetchCategoryTree(locale.value);

	const fetchedCategory = await getCurrentCategory(
		{
			categoriesSlug: route.params.slug as string[]
		},
		{ categoryTree, currentCategoryId: useCategoryStore().getCategoryId! }
	);

	if (!fetchedCategory) {
		return showError({
			statusCode: 404,
			statusMessage: t("errors.page_not_found.title"),
			message: t("errors.page_not_found.message")
		});
	}

	if (!route.params.slug?.includes(kebabCase(fetchedCategory.Name))) {
		router.push(localePath(`/${kebabCase(fetchedCategory.Name)}`));
	}
	currentCategory.value = fetchedCategory;

	setCategoryBreadcrumb(
		getCategoryDetailsById(
			categoryTree,
			fetchedCategory.CategoryId,
			"CategoryId"
		)
	);
	setPageTitle(fetchedCategory.Name);

	useCategoryStore().setCategoryId(fetchedCategory.CategoryId);

	const contentPath = `${config.public.categoryContentKey}/${fetchedCategory?.CategoryId}`;
	try {
		const { data } = await fetchStoryblokContentByFolder(
			storyblokApi,
			contentPath,
			{
				version: getStoryblokVersion(),
				resolve_links: "url",
				language: localeProperties.value.iso
			},
			{
				storyblokFolder: config.public.storyblokFolder,
				storyblokFallbackFolder: config.public.storyblokFallbackFolder
			}
		);
		storyblokDataRef.value = data?.story;
	} catch {
		storyblokDataRef.value = null;
	}
};

const fetchProductsData = async () => {
	if (!locale.value || !currentCategory.value) {
		return;
	}

	if (storyblokDataRef.value?.content?.row_view) {
		pageSize.value = "";
	}

	const allParametrics = await fetchAllParametrics();
	const [filters, products] = await Promise.all([
		mapProductFilters(
			{
				categorySeed: currentCategory.value.CategoryId.toString(),
				cultureCode: localeProperties.value.iso
			},
			{
				allParametrics
			}
		),
		listProductByFilters({
			categorySeed: currentCategory.value.CategoryId.toString(),
			pageNo: "1",
			pageSize: pageSize.value,
			sort: initialCurrentSortingOrder.value,
			statusSeed: "1",
			cultureCode: localeProperties.value.iso,
			...(route.query.filter ? { filter: route.query.filter as string } : {})
		})
	]);

	initialData.value = { products, ...filters };
};
const fetchNorceData = async () => {
	await fetchCategoryData();

	if (!storyblokDataRef.value?.content?.hide_products) {
		await fetchProductsData();
	} else {
		initialData.value = { products: [], filters: [] };
	}
};

const categoryWidgets = ref<BlokCategoryWidget[]>([]);
const preFooter = ref<BlokColumns[]>([]);
const getInitialDataFromStoryBlok = async () => {
	const { data } = await storyblokApi.get(
		buildStoryblokUrl("category-page-layout"),
		{
			version: getStoryblokVersion(),
			resolve_links: "url",
			language: localeProperties.value.iso
		}
	);

	categoryWidgets.value = data.story.content.filters;
	preFooter.value = data.story.content.pre_footer;
	pageSize.value = data.story.content.products_page_count
		? data.story.content.products_page_count
		: "12";
	pictureAspectRatio.value = data.story.content.images_aspect_ratio;
	seo.value = data.story.content.seo;
	recommendations.value = data.story.content.recommendations?.[0];
};

await getInitialDataFromStoryBlok();
await fetchNorceData();

const seoImage = computed(() => {
	if (currentCategory.value?.ImageKey) {
		return ImagesCdnUrl + currentCategory.value?.ImageKey;
	} else if (seo.value?.og_image) {
		return seo.value?.og_image;
	}

	return null;
});

const requestURL = useRequestURL();
const domain = computed(() => requestURL.origin);

const productItemList = computed(() => {
	if (!initialData.value?.products?.Items?.length) {
		return null;
	}

	return defineItemList({
		numberOfItems: initialData.value.products.ItemCount || 0,
		itemListElement: initialData.value.products.Items.slice(0, 10).map(
			(product, index) => ({
				"@type": "ListItem",
				position: index + 1,
				name: product.Name || product.VariantName || "",
				url: product.UniqueName
					? `${domain.value}${localePath(`/${product.UniqueName}`)}`
					: undefined
			})
		)
	});
});

useSeoMeta({
	title: () => `${currentCategory.value?.Name}`,
	ogTitle: () => `${currentCategory.value?.Name}`,
	description: seo.value?.description,
	ogDescription: seo.value?.og_description
});
if (import.meta.server) {
	const appStore = useAppStore();
	if (seoImage.value) {
		defineOgImageComponent("OgCategory", {
			image: seoImage.value,
			logo: appStore.siteLogo?.filename || "",
			title: seo.value?.og_title || "",
			description: seo.value?.description || "",
			width: 1200,
			height: 630
		});
	}
}

useSchemaOrg([
	defineWebPage({
		"@type": "CollectionPage",
		name: currentCategory.value?.Name || "",
		description: seo.value?.description || "",
		image: seoImage.value || undefined,
		...(productItemList.value ? { mainEntity: productItemList.value } : {})
	})
]);

watch(pageSize, () => {
	fetchNorceData();
});
</script>
